/**
 * @file pages/admin/CountryGuideManager.jsx
 * @description Admin country guide management page. Provides CRUD operations
 * for destination guides with content editing, image management, and previews.
 */

import React, { useState, useEffect } from 'react';
import { 
  getAllCountries, 
  countryGuides, 
  updateCountryGuide, 
  addNewCountry, 
  deleteCountry 
} from '../../data/countryGuides';

const CountryGuideManager = () => {
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [formData, setFormData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [newCountryCode, setNewCountryCode] = useState('');

  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = () => {
    const allCountries = getAllCountries();
    setCountries(allCountries);
  };

  const handleCountrySelect = (code) => {
    const countryData = countryGuides[code];
    setSelectedCountry(code);
    setFormData(countryData);
    setIsEditing(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDestinationChange = (index, field, value) => {
    const updatedDestinations = [...formData.popularDestinations];
    updatedDestinations[index] = {
      ...updatedDestinations[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      popularDestinations: updatedDestinations
    }));
  };

  const handleHighlightChange = (index, value) => {
    const updatedHighlights = [...formData.highlights];
    updatedHighlights[index] = value;
    setFormData(prev => ({
      ...prev,
      highlights: updatedHighlights
    }));
  };

  const handleSave = () => {
    if (selectedCountry) {
      updateCountryGuide(selectedCountry, formData);
      setIsEditing(false);
      loadCountries();
    }
  };

  const handleAddNewCountry = () => {
    if (newCountryCode && newCountryCode.length === 2) {
      const success = addNewCountry(newCountryCode.toLowerCase(), {
        name: '',
        overview: '',
        bestTimeToVisit: '',
        visaRequirements: '',
        popularDestinations: [
          { name: '', description: '' },
          { name: '', description: '' },
          { name: '', description: '' }
        ],
        culture: '',
        cuisine: '',
        transportation: '',
        accommodation: '',
        budgetInfo: '',
        safetyTips: '',
        highlights: ['', '', '', '', '', '']
      });

      if (success) {
        loadCountries();
        handleCountrySelect(newCountryCode.toLowerCase());
        setNewCountryCode('');
        setIsEditing(true);
      }
    }
  };

  const handleDeleteCountry = () => {
    if (selectedCountry && window.confirm('Are you sure you want to delete this country guide?')) {
      deleteCountry(selectedCountry);
      setSelectedCountry(null);
      setFormData(null);
      loadCountries();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDFDFF] to-[#F0F0F5] p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-[#243185] mb-8">Country Guide Manager</h1>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Country List */}
          <div className="bg-white rounded-lg shadow-lg p-4">
            <div className="mb-4">
              <input
                type="text"
                placeholder="New country code (2 letters)"
                className="w-full px-3 py-2 border rounded-lg mb-2"
                value={newCountryCode}
                onChange={(e) => setNewCountryCode(e.target.value.toUpperCase())}
                maxLength={2}
              />
              <button
                onClick={handleAddNewCountry}
                className="w-full bg-gradient-to-r from-[#5365dc] to-[#243185] text-white px-4 py-2 rounded-lg"
              >
                Add New Country
              </button>
            </div>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {countries.map(country => (
                <button
                  key={country.code}
                  onClick={() => handleCountrySelect(country.code)}
                  className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                    selectedCountry === country.code
                      ? 'bg-[#5365dc] text-white'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  {country.name || country.code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Edit Form */}
          {formData && (
            <div className="md:col-span-3 bg-white rounded-lg shadow-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold text-[#243185]">
                  {isEditing ? 'Edit Country Guide' : 'View Country Guide'}
                </h2>
                <div className="space-x-4">
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    className="px-4 py-2 rounded-lg border border-[#5365dc] text-[#5365dc] hover:bg-[#5365dc] hover:text-white transition-colors"
                  >
                    {isEditing ? 'Cancel' : 'Edit'}
                  </button>
                  {isEditing && (
                    <button
                      onClick={handleSave}
                      className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#5365dc] to-[#243185] text-white"
                    >
                      Save Changes
                    </button>
                  )}
                  <button
                    onClick={handleDeleteCountry}
                    className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300"
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Country Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Overview
                    </label>
                    <textarea
                      value={formData.overview}
                      onChange={(e) => handleInputChange('overview', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border rounded-lg"
                      rows={3}
                    />
                  </div>
                </div>

                {/* Travel Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Best Time to Visit
                    </label>
                    <textarea
                      value={formData.bestTimeToVisit}
                      onChange={(e) => handleInputChange('bestTimeToVisit', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border rounded-lg"
                      rows={2}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Visa Requirements
                    </label>
                    <textarea
                      value={formData.visaRequirements}
                      onChange={(e) => handleInputChange('visaRequirements', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border rounded-lg"
                      rows={2}
                    />
                  </div>
                </div>

                {/* Popular Destinations */}
                <div>
                  <h3 className="text-lg font-medium text-[#243185] mb-2">
                    Popular Destinations
                  </h3>
                  <div className="space-y-4">
                    {formData.popularDestinations.map((dest, index) => (
                      <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                          type="text"
                          value={dest.name}
                          onChange={(e) => handleDestinationChange(index, 'name', e.target.value)}
                          disabled={!isEditing}
                          placeholder="Destination name"
                          className="w-full px-3 py-2 border rounded-lg"
                        />
                        <input
                          type="text"
                          value={dest.description}
                          onChange={(e) => handleDestinationChange(index, 'description', e.target.value)}
                          disabled={!isEditing}
                          placeholder="Description"
                          className="w-full px-3 py-2 border rounded-lg"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Highlights */}
                <div>
                  <h3 className="text-lg font-medium text-[#243185] mb-2">
                    Highlights
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {formData.highlights.map((highlight, index) => (
                      <input
                        key={index}
                        type="text"
                        value={highlight}
                        onChange={(e) => handleHighlightChange(index, e.target.value)}
                        disabled={!isEditing}
                        placeholder={`Highlight ${index + 1}`}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    ))}
                  </div>
                </div>

                {/* Additional Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Culture
                    </label>
                    <textarea
                      value={formData.culture}
                      onChange={(e) => handleInputChange('culture', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border rounded-lg"
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cuisine
                    </label>
                    <textarea
                      value={formData.cuisine}
                      onChange={(e) => handleInputChange('cuisine', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border rounded-lg"
                      rows={3}
                    />
                  </div>
                </div>

                {/* Practical Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Transportation
                    </label>
                    <textarea
                      value={formData.transportation}
                      onChange={(e) => handleInputChange('transportation', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border rounded-lg"
                      rows={2}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Accommodation
                    </label>
                    <textarea
                      value={formData.accommodation}
                      onChange={(e) => handleInputChange('accommodation', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border rounded-lg"
                      rows={2}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Budget Information
                    </label>
                    <textarea
                      value={formData.budgetInfo}
                      onChange={(e) => handleInputChange('budgetInfo', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border rounded-lg"
                      rows={2}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Safety Tips
                    </label>
                    <textarea
                      value={formData.safetyTips}
                      onChange={(e) => handleInputChange('safetyTips', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border rounded-lg"
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CountryGuideManager; 